// A From/To pair of calendar days → the instant range that covers both days
// whole, in the operator's zone (BL-1820, ADR 024 — Time Model).
import { parseLogicalDate } from './parse-logical-date';
import { toLogicalDateString } from './to-logical-date-string';
import { wallTimeToInstant } from './wall-time-to-instant';

/** The half-open instant range `[fromInstant, toExclusiveInstant)`; `null` = unbounded. */
export interface CalendarDayInstantRange {
    fromInstant: string | null;
    toExclusiveInstant: string | null;
}

/**
 * Resolve a `YYYY-MM-DD` From / To pair to the instants that bound a
 * `timestamptz` filter, with **both days inclusive**: From is the start of its
 * day in `zone`, To is the start of the day AFTER it. Querying
 * `created_at >= fromInstant AND created_at < toExclusiveInstant` then keeps a
 * row sent at 23:59 on the To day.
 *
 * Each edge resolves against its own date's offset, so a range that crosses a
 * DST change is not an hour short. A logical date never passes through a `Date`
 * here (logical-dates.md): the next day is calendar arithmetic, and only the
 * wall-clock midnight is converted.
 *
 * @param input.from - The first day, `YYYY-MM-DD`; empty or malformed = no lower bound.
 * @param input.to - The last day (inclusive), `YYYY-MM-DD`; empty or malformed = no upper bound.
 * @param input.zone - The IANA zone the operator reads days in.
 * @returns The ISO instants, `null` for an open end.
 * @sideEffect none — pure.
 */
export function calendarDayRangeToInstants({
    from,
    to,
    zone,
}: {
    from: string;
    to: string;
    zone: string;
}): CalendarDayInstantRange {
    const fromDay = parseLogicalDate(from);
    const toDay = parseLogicalDate(to);
    return {
        fromInstant: fromDay ? startOfDay(toLogicalDateString(fromDay), zone) : null,
        toExclusiveInstant: toDay ? startOfDay(nextDay(toDay), zone) : null,
    };
}

function startOfDay(day: string, zone: string): string {
    return wallTimeToInstant({ wallDateTime: `${day}T00:00`, zone });
}

/** The calendar day after `day`, rolling months and years. */
function nextDay(day: { year: number; month: number; day: number }): string {
    // `Date.UTC` as a pure calendar oracle, read back with getUTC* — no local lens.
    const probe = new Date(Date.UTC(day.year, day.month - 1, day.day + 1));
    return toLogicalDateString({
        year: probe.getUTCFullYear(),
        month: probe.getUTCMonth() + 1,
        day: probe.getUTCDate(),
    });
}
